import axios, { AxiosError } from "axios";
import { useMutation, useQueryClient } from "react-query";
import { HttpClientError } from "../../../../providers/HttpClient";
import { invalidateAttendanceViews } from "./attendanceCache";
import {
  SingleShiftClockRequest,
  SingleShiftClockResponse,
  BulkShiftClockRequest,
  ShiftClockResponse,
  ShiftClockCheckResponse,
} from "../model/ShiftClock";
import { NoShowOverrideRequest } from "../request/NoShowOverrideRequest";

const ShiftClockApiService = async (
  request: SingleShiftClockRequest
): Promise<SingleShiftClockResponse> => {
  try {
    const { data } = await axios.post("/shift-clocks/", request);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
      throw error;
    }
    throw error;
  }
};

const BulkShiftClockApiService = async (
  request: BulkShiftClockRequest
): Promise<ShiftClockResponse> => {
  try {
    const { data } = await axios.post("/shift-clocks/bulk/", request);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
      throw error;
    }
    throw error;
  }
};

const CheckShiftClockApiService = async (
  request: BulkShiftClockRequest
): Promise<ShiftClockCheckResponse> => {
  try {
    const { data } = await axios.post("/shift-clocks/check/", request);
    return data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
      throw error;
    }
    throw error;
  }
};

const useCreateShiftClockApiService = (): ReturnType<
  typeof useMutation<
    SingleShiftClockResponse,
    AxiosError,
    SingleShiftClockRequest
  >
> => {
  const queryClient = useQueryClient();
  return useMutation<
    SingleShiftClockResponse,
    AxiosError,
    SingleShiftClockRequest
  >((request) => ShiftClockApiService(request), {
    onSuccess: () => invalidateAttendanceViews(queryClient),
  });
};

const useCreateBulkShiftClockApiService = (): ReturnType<
  typeof useMutation<ShiftClockResponse, AxiosError, BulkShiftClockRequest>
> => {
  const queryClient = useQueryClient();
  return useMutation<ShiftClockResponse, AxiosError, BulkShiftClockRequest>(
    (request) => BulkShiftClockApiService(request),
    {
      onSuccess: () => invalidateAttendanceViews(queryClient),
    }
  );
};

const useCheckShiftClockApiService = (): ReturnType<
  typeof useMutation<ShiftClockCheckResponse, AxiosError, BulkShiftClockRequest>
> =>
  useMutation<ShiftClockCheckResponse, AxiosError, BulkShiftClockRequest>(
    (request) => CheckShiftClockApiService(request)
  );

const NoShowOverrideApiService = async (
  request: NoShowOverrideRequest
): Promise<void> => {
  try {
    await axios.post("/shift-clocks/no_show/", request);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      HttpClientError(error);
      throw error;
    }
    throw error;
  }
};

const useNoShowOverrideApiService = (): ReturnType<
  typeof useMutation<void, AxiosError, NoShowOverrideRequest>
> => {
  const queryClient = useQueryClient();
  return useMutation<void, AxiosError, NoShowOverrideRequest>(
    (request) => NoShowOverrideApiService(request),
    {
      onSuccess: () => invalidateAttendanceViews(queryClient),
    }
  );
};

export {
  ShiftClockApiService,
  BulkShiftClockApiService,
  CheckShiftClockApiService,
  NoShowOverrideApiService,
  useCreateShiftClockApiService,
  useCreateBulkShiftClockApiService,
  useCheckShiftClockApiService,
  useNoShowOverrideApiService,
};
